/**
 * WH lokale bezorging (14-10-2026)
 *
 * Postcodecheck voor <wh-lokale-bezorging> (snippets/wh-lokale-bezorging.liquid).
 * De uitkomst komt als data-wh-lb="ja|nee" op <html>; alle teksten staan al in de
 * Liquid-markup en worden met CSS getoond. Zo blijft de uitkomst staan als de
 * winkelwagenlade opnieuw wordt opgebouwd (morph).
 *
 * Postcode en uitkomst worden in localStorage bewaard (sleutel 'wh-lokale-bezorging').
 * Alle opslag staat in try/catch: zonder opslag werkt de check gewoon, alleen zonder geheugen.
 *
 * Bezorggebied gemeten via Shopify (draftOrderAvailableDeliveryOptions, 13-10-2026):
 * 2012, 2014 (deel Haarlem-Zuid), 2101 t/m 2106 (Heemstede), 2111 (Aerdenhout), 2116 (Bentveld).
 * Het gebied in Shopify kan een straal zijn: de uitkomst is dus een indicatie.
 */

const OPSLAG = 'wh-lokale-bezorging';
const LOKAAL = new Set(['2012', '2014', '2101', '2102', '2103', '2104', '2105', '2106', '2111', '2116']);
const PATROON = /^\s*([1-9]\d{3})\s*([a-zA-Z]{2})?\s*$/;

const TEKST = {
  fout: 'Vul een geldige postcode in, bijvoorbeeld 2106 AM.',
  ja: 'U woont in ons voordelige bezorggebied. Bezorging kost 5 euro en is gratis vanaf 50 euro.',
  nee: 'Op deze postcode geldt onze gewone bezorging met de pakketdienst: 6,95 euro, gratis vanaf 99 euro.',
};

/**
 * @param {string} invoer
 * @returns {{ postcode: string, lokaal: boolean } | null}
 */
function controleer(invoer) {
  const match = PATROON.exec(String(invoer || ''));
  if (!match) return null;
  const cijfers = match[1] ?? '';
  const letters = (match[2] || '').toUpperCase();
  return { postcode: letters ? `${cijfers} ${letters}` : cijfers, lokaal: LOKAAL.has(cijfers) };
}

/** @returns {{ postcode: string, lokaal: boolean } | null} */
function lees() {
  try {
    const ruw = window.localStorage.getItem(OPSLAG);
    if (!ruw) return null;
    const data = JSON.parse(ruw);
    if (!data || typeof data.postcode !== 'string') return null;
    return controleer(data.postcode);
  } catch {
    return null;
  }
}

/** @param {{ postcode: string, lokaal: boolean }} uitkomst */
function bewaar(uitkomst) {
  try {
    window.localStorage.setItem(OPSLAG, JSON.stringify(uitkomst));
  } catch {
    /* geen opslag beschikbaar: de check werkt toch */
  }
}

/** @param {{ postcode: string, lokaal: boolean } | null} uitkomst */
function zetStaat(uitkomst) {
  const html = document.documentElement;
  if (!uitkomst) {
    html.removeAttribute('data-wh-lb');
    return;
  }
  html.setAttribute('data-wh-lb', uitkomst.lokaal ? 'ja' : 'nee');
  for (const el of document.querySelectorAll('.wh-lb-pc')) {
    if (el.textContent !== uitkomst.postcode) el.textContent = uitkomst.postcode;
  }
  for (const input of document.querySelectorAll('.wh-lb-input')) {
    if (input instanceof HTMLInputElement && document.activeElement !== input && !input.value) {
      input.value = uitkomst.postcode;
    }
  }
}

class WhLokaleBezorging extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', (event) => this.#klik(event), { signal });
    this.addEventListener('keydown', (event) => this.#toets(event), { signal });
    zetStaat(lees());
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {MouseEvent} event */
  #klik(event) {
    const knop = event.target instanceof Element ? event.target.closest('button') : null;
    if (!knop || !this.contains(knop)) return;
    if (knop.classList.contains('wh-lb-knop')) this.check();
    else if (knop.classList.contains('wh-lb-wijzig')) this.wijzig();
  }

  /** @param {KeyboardEvent} event */
  #toets(event) {
    const doel = event.target;
    if (event.key !== 'Enter' || !(doel instanceof HTMLInputElement) || !doel.classList.contains('wh-lb-input')) return;
    // Voorkomt dat Enter een omliggend formulier (product- of winkelwagenformulier) verstuurt.
    event.preventDefault();
    this.check();
  }

  /** Controleert de ingevulde postcode en toont de uitkomst. */
  check() {
    const input = this.querySelector('.wh-lb-input');
    const fout = this.querySelector('.wh-lb-fout');
    if (!(input instanceof HTMLInputElement)) return;

    const uitkomst = controleer(input.value);
    if (!uitkomst) {
      input.setAttribute('aria-invalid', 'true');
      if (fout) fout.textContent = TEKST.fout;
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');
    if (fout) fout.textContent = '';
    input.value = uitkomst.postcode;
    bewaar(uitkomst);
    this.classList.remove('wh-lb--wijzig');
    zetStaat(uitkomst);
    for (const ander of document.querySelectorAll('.wh-lb-input')) {
      if (ander instanceof HTMLInputElement && ander !== input) ander.value = uitkomst.postcode;
    }
    this.#meld(uitkomst.lokaal ? TEKST.ja : TEKST.nee);

    // Het formulier wordt verborgen: zet de focus op de knop om een andere postcode te kiezen.
    const wijzig = this.querySelector('.wh-lb-wijzig');
    if (wijzig instanceof HTMLElement) wijzig.focus();
  }

  /** Toont het postcodeveld weer. */
  wijzig() {
    this.classList.add('wh-lb--wijzig');
    const input = this.querySelector('.wh-lb-input');
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }

  /** @param {string} tekst */
  #meld(tekst) {
    const status = this.querySelector('.wh-lb-status');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = tekst;
    });
  }
}

if (!customElements.get('wh-lokale-bezorging')) {
  customElements.define('wh-lokale-bezorging', WhLokaleBezorging);
}
